import { getAccessToken } from './authService';

export const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface CardOwnership {
  userId: string;
  cardId: string;
  setId: string;
  owned: boolean;
  quantity: number;
  condition?: string;
  purchasePrice?: number;
  purchaseDate?: string;
  salePrice?: number;
  saleDate?: string;
  notes?: string;
  location?: string;
  language?: string;
  disposition?: 'sold' | 'traded' | 'gifted' | null;
  tradedFor?: string;
  gradingCompany?: string;
  gradingGrade?: string;
  gradingId?: string;
  isGrading?: boolean;
  inCollection?: boolean;
  isWishlist?: boolean;
  isFavorite?: boolean;
  links?: string[];
  purchasedFrom?: string;
  marketPriceAtPurchase?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface CardOwnershipInput {
  setId: string;
  owned: boolean;
  quantity?: number;
  condition?: string;
  purchasePrice?: number;
  purchaseDate?: string;
  salePrice?: number;
  saleDate?: string;
  notes?: string;
  location?: string;
  language?: string;
  disposition?: 'sold' | 'traded' | 'gifted' | null;
  gradingCompany?: string;
  gradingGrade?: string;
  gradingId?: string;
  isGrading?: boolean;
  inCollection?: boolean;
  isWishlist?: boolean;
  isFavorite?: boolean;
  links?: string[];
  purchasedFrom?: string;
  marketPriceAtPurchase?: number;
}

/**
 * Get authorization headers with JWT token
 */
export async function getAuthHeaders(): Promise<HeadersInit> {
  const token = await getAccessToken();

  if (!token) {
    console.warn('CardOwnershipService - No access token available');
  } else {
    console.log('CardOwnershipService - Got access token:', {
      hasToken: !!token,
      tokenPreview: token.substring(0, 30) + '...',
      tokenLength: token.length,
    });
  }

  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

/**
 * Get all owned cards for the current user
 */
export async function getUserCardOwnerships(
  setId?: string,
  includePricing: boolean = false,
  source: 'master' | 'collection' = 'master'
): Promise<CardOwnership[]> {
  try {
    const headers = await getAuthHeaders();
    let url = `${API_BASE_URL}/api/pokemon/ownership`;
    const params = new URLSearchParams();
    
    if (setId) params.append('setId', setId);
    if (includePricing) params.append('includePricing', 'true');
    if (source) params.append('source', source);
    
    const queryString = params.toString();
    if (queryString) {
        url += `?${queryString}`;
    }

    console.log('CardOwnershipService - Fetching ownerships:', {
      url,
      hasAuthHeader: !!(headers as any).Authorization,
    });

    const response = await fetch(url, { headers });

    console.log('CardOwnershipService - Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      console.error('CardOwnershipService - Error response:', {
        status: response.status,
        errorData,
      });
      throw new Error(
        `Failed to fetch card ownerships: ${response.statusText}`
      );
    }

    const data = await response.json();
    console.log('CardOwnershipService - Success:', {
      ownershipCount: data.ownerships?.length || 0,
    });
    return data.ownerships || [];
  } catch (error) {
    console.error(
      'CardOwnershipService - Error fetching card ownerships:',
      error
    );
    throw error;
  }
}

/**
 * Get progress for all sets
 */
export async function getUserSetProgress(): Promise<Record<string, number>> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/pokemon/ownership/progress`, {
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch set progress: ${response.statusText}`);
    }

    const data = await response.json();
    return data.progress || {};
  } catch (error) {
    console.error('Error fetching set progress:', error);
    throw error;
  }
}

/**
 * Force recalculate set progress
 */
export async function syncSetProgress(): Promise<void> {
  try {
    console.log('[FRONTEND] Calling sync progress API...');
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE_URL}/api/pokemon/ownership/progress/sync`,
      {
        method: 'POST',
        headers,
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to sync set progress: ${response.statusText}`);
    }
    console.log('[FRONTEND] Sync progress API succeeded');
  } catch (error) {
    console.error('Error syncing set progress:', error);
    throw error;
  }
}

/**
 * Get ownership for a specific card
 */
export async function getCardOwnership(
  cardId: string
): Promise<CardOwnership | null> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/pokemon/ownership/${cardId}`, {
      headers,
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch card ownership: ${response.statusText}`);
    }

    const data = await response.json();
    return data.ownership || null;
  } catch (error) {
    console.error('Error fetching card ownership:', error);
    throw error;
  }
}

/**
 * Create or update card ownership
 */
export async function upsertCardOwnership(
  cardId: string,
  ownershipData: CardOwnershipInput
): Promise<CardOwnership> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/pokemon/ownership/${cardId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({
        setId: ownershipData.setId,
        owned: ownershipData.owned,
        quantity: ownershipData.quantity,
        condition: ownershipData.condition,
        purchasePrice: ownershipData.purchasePrice,
        purchaseDate: ownershipData.purchaseDate,
        salePrice: ownershipData.salePrice,
        saleDate: ownershipData.saleDate,
        notes: ownershipData.notes,
        location: ownershipData.location,
        disposition: ownershipData.disposition,
        gradingCompany: ownershipData.gradingCompany,
        gradingGrade: ownershipData.gradingGrade,
        gradingId: ownershipData.gradingId,
        isGrading: ownershipData.isGrading,
        inCollection: ownershipData.inCollection,
        isWishlist: ownershipData.isWishlist,
        isFavorite: ownershipData.isFavorite,
        links: ownershipData.links,
      }),
    });

    if (!response.ok) {
      throw new Error(
        `Failed to update card ownership: ${response.statusText}`
      );
    }

    const data = await response.json();
    return data.ownership;
  } catch (error) {
    console.error('Error updating card ownership:', error);
    throw error;
  }
}

/**
 * Delete card ownership
 */
export async function deleteCardOwnership(cardId: string): Promise<void> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/pokemon/ownership/${cardId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      throw new Error(
        `Failed to delete card ownership: ${response.statusText}`
      );
    }
  } catch (error) {
    console.error('Error deleting card ownership:', error);
    throw error;
  }
}

/**
 * Toggle card ownership (simple owned/not owned)
 */
export async function toggleCardOwnership(
  cardId: string
): Promise<CardOwnership> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE_URL}/api/pokemon/ownership/${cardId}/toggle`,
      {
        method: 'POST',
        headers,
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to toggle card ownership: ${response.statusText}`
      );
    }

    const data = await response.json();
    return data.ownership;
  } catch (error) {
    console.error('Error toggling card ownership:', error);
    throw error;
  }
}

/**
 * Toggle card wishlist status
 */
export async function toggleCardWishlist(
  cardId: string
): Promise<CardOwnership> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE_URL}/api/pokemon/ownership/${cardId}/wishlist/toggle`,
      {
        method: 'POST',
        headers,
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to toggle card wishlist: ${response.statusText}`
      );
    }

    const data = await response.json();
    return data.ownership;
  } catch (error) {
    console.error('Error toggling card wishlist:', error);
    throw error;
  }
}

/**
 * Toggle card favorite status
 */
export async function toggleCardFavorite(
  cardId: string
): Promise<CardOwnership> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE_URL}/api/pokemon/ownership/${cardId}/favorite/toggle`,
      {
        method: 'POST',
        headers,
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to toggle card favorite: ${response.statusText}`
      );
    }

    const data = await response.json();
    return data.ownership;
  } catch (error) {
    console.error('Error toggling card favorite:', error);
    throw error;
  }
}

/**
 * Bulk update card ownerships
 */
export async function bulkUpsertCardOwnerships(
  ownerships: Array<{ cardId: string } & CardOwnershipInput>
): Promise<CardOwnership[]> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/pokemon/ownership/bulk`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        ownerships: ownerships.map((o) => ({
          cardId: o.cardId,
          setId: o.setId,
          owned: o.owned,
          quantity: o.quantity,
          condition: o.condition,
          purchasePrice: o.purchasePrice,
          purchaseDate: o.purchaseDate,
          salePrice: o.salePrice,
          saleDate: o.saleDate,
          notes: o.notes,
          location: o.location,
          disposition: o.disposition,
          gradingCompany: o.gradingCompany,
          gradingGrade: o.gradingGrade,
          gradingId: o.gradingId,
          isGrading: o.isGrading,
          inCollection: o.inCollection,
          isWishlist: o.isWishlist,
          isFavorite: o.isFavorite,
          links: o.links,
        })),
      }),
    });

    if (!response.ok) {
      throw new Error(
        `Failed to bulk update card ownerships: ${response.statusText}`
      );
    }

    const data = await response.json();
    return data.results || [];
  } catch (error) {
    console.error('Error bulk updating card ownerships:', error);
    throw error;
  }
}

/**
 * Convert card ownerships array to map for easy lookup
 */
export function ownershipsToMap(
  ownerships: CardOwnership[]
): Record<string, CardOwnership> {
  return ownerships.reduce((map, ownership) => {
    map[ownership.cardId] = ownership;
    return map;
  }, {} as Record<string, CardOwnership>);
}
